import { push } from 'connected-react-router';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';

import * as paths from 'src/app/router/paths';
import { accountLoadEverything } from 'src/app/state/actions/account';
import {
  createGetDeliveryAddressesSelector,
  getAccountDefaultCardId,
  getAccountIDVerificationStatus,
  getBFFCart,
  getBFFCartDeliveryAddress,
  getBFFCartDeliveryMethod,
  getBFFCartPaymentMethod,
  getCurrentCartId,
  getIsAllergyProfileComplete,
  getIsUserESIMailEligible,
  getOrderNeedsCreditCart,
  getPaymentCards,
  getUserIsLoginSet,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { DeliveryMethodLabels } from 'src/consts';
import { ID_UPLOAD_STATUS } from 'src/consts';

import {
  COMPLETE_CHECKOUT_CONTACT_INFO,
  COMPLETE_CHECKOUT_TRANSFER,
  SET_SELECTED_DELIVERY_METHOD_LABEL_FROM_CHECKOUT,
} from './types';

/**
 * # pushNextCheckoutState
 *
 * Logic for getting through the checkout flow, using the
 * `v2/cart` endpoint.
 */
export function pushNextCheckoutState() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    /**
     * It is extremely important to fetch all data again before inspecting the store to decide the next step in the
     * checkout flow. There can easily be race conditions otherwise.
     */
    await dispatch(accountLoadEverything());

    const state = getState();

    const cartId = getCurrentCartId(state);
    const getDeliveryAddresses = createGetDeliveryAddressesSelector();
    const paymentCards = getPaymentCards(state);

    // expect the cart to be loaded as this is a post order action redirection
    const bffCart = getBFFCart(state);
    const selectedDeliveryMethod = getBFFCartDeliveryMethod(state);
    const needsCreditCard = getOrderNeedsCreditCart(state);

    const isUserESIMailEligible = getIsUserESIMailEligible(state);

    // Delivery Address
    // Check if one is selected || Fallback to default address.
    const deliveryAddresses = getDeliveryAddresses(state);
    const defaultAddress = filter(deliveryAddresses, e => e.attributes.is_default)[0];

    const defaultAddressId = defaultAddress?.id;
    const selectedDeliveryAddress = getBFFCartDeliveryAddress(state);

    const selectedCardId = getBFFCartPaymentMethod(state)?.id;
    const defaultCardId = selectedCardId || getAccountDefaultCardId(state);

    // Allergy Profile
    const isAllergyProfileComplete = getIsAllergyProfileComplete(state);

    // order of importance: address > card > window > allergies > ID upload > account
    const idVerificationRequired = bffCart.verifyId;
    const idVerificationStatus = getAccountIDVerificationStatus(state);
    const accountSetupRequired = !getUserIsLoginSet(state);
    // Order of importance:
    // 1. address >
    // 2. card >
    // 3. window >
    // 4. allergies >
    // 5. ID upload >
    // 6. account ?
    // 7. prescription transfer (otc promo only)
    // 8. first name, last name, mobile number signup (otc promo only)
    if (!defaultAddressId) {
      if (!selectedDeliveryAddress) {
        if (isEmpty(getDeliveryAddresses(state))) {
          return dispatch(push(paths.checkoutAddAddress.toPath({ cartId })));
        } else {
          return dispatch(push(paths.checkoutSelectAddress.toPath({ cartId })));
        }
      }
    }

    if (!selectedDeliveryMethod) {
      if (!isUserESIMailEligible) {
        return dispatch(push(paths.checkoutSelectWindow.toPath({ cartId })));
      } else {
        return dispatch(push(paths.checkoutSelectDeliveryMethod.toPath({ cartId })));
      }
    }

    if (needsCreditCard) {
      if (!defaultCardId) {
        if (!selectedCardId) {
          if (isEmpty(paymentCards)) {
            return dispatch(push(paths.checkoutAddCreditCard.toPath({ cartId })));
          } else {
            return dispatch(push(paths.checkoutSelectCreditCard.toPath({ cartId })));
          }
        }
      }
    }

    if (!isAllergyProfileComplete) {
      return dispatch(push(paths.checkoutAllergyProfile.toPath({ cartId })));
    }

    if (idVerificationRequired && idVerificationStatus === ID_UPLOAD_STATUS.unverified) {
      return dispatch(push(paths.checkoutAddIdentification.toPath({ cartId })));
    }

    if (accountSetupRequired) {
      return dispatch(push(paths.checkoutAccountSetup.toPath({ cartId })));
    }

    return dispatch(push(paths.checkout.toPath()));
  };
}

export function setCheckoutTransfer() {
  return {
    type: COMPLETE_CHECKOUT_TRANSFER,
  };
}

export function setCheckoutContactInfo() {
  return {
    type: COMPLETE_CHECKOUT_CONTACT_INFO,
  };
}

export function setSelectedDeliveryMethodLabelFromCheckout(selectedDeliveryMethodLabel: DeliveryMethodLabels) {
  return {
    type: SET_SELECTED_DELIVERY_METHOD_LABEL_FROM_CHECKOUT,
    selectedDeliveryMethodLabel,
  };
}
